// import parseNumber from "@tecumseh/utilities/parseNumber";
import Color from "color";

const STATIC_PATH = "/userfiles/_static/store";

// -- Colors

const swatch = {
    white: "#FFFFFF",
    whiteSmoke: "#efefef",
    whiteSnow: "#f9f9f9",
    whiteSaltpan: "#f6f7f8",
    darkWhite: "#fdfdfd",
    black: "#000000",
    green: "#8AD902",
    neutralLight: "#F0F2F7",
    darkBlue: "#2e64b0",
    neutralMedium: "#CACFDE",
    primaryBlue: "#006ED2",
    secondaryNavyOne: "#134078",
    secondaryNavyThree: "#112945",
    solitude: "#F0F2F7",
    darkCyanBlue: "#112945",
    yellow: "#EDB800",
    mango: "#ffc808",
    red: "#FF0000",
    lightGreen: "#00B900",
    alertMsgRed: "#ED0000",
    blue: "#0000FF",
    rockBlue: "#9DB1C9",
    whisper: "#eaeaea",
    lightGrayishBlue: "#f0f7fd",
    grey: {
        normalGray: "#808080",
        darkGray: "#cccccc",
        lighterGrayShade: "#f4f6fa",
        lighter: "#F6F6F6",
        light: "#F3F5F7",
        slightlyLightGray: "#d3d3d3",
        base: "#636161",
        dark: "#E0DFE0",
        darker: "#000000",
        shade: "#F1F6FB",
        graySwan: "#b8b8b8",
        grayWhisper: "#e8e8e8",
        gainsboro: "#dedede",
        grayShade: "#F2F2F2",
        lightGrey: "#F9F9F9",
        darkGrey: "#F1F2F3",
        fuscousGrey: "#393a3a",
        dimGray: "#666666",
    },
    lightPurple: "#5c56a3",
};

export const COLORS = {
    white: swatch.white,
    black: swatch.black,
    green: swatch.green,
    darkBlue: swatch.darkBlue,
    normalGray: swatch.grey.normalGray,
    slightlyLightGray: swatch.grey.slightlyLightGray,
    fuscousGrey: swatch.grey.fuscousGrey,
    neutralLight: swatch.neutralLight,
    neutralMedium: swatch.neutralMedium,
    primaryBlue: swatch.primaryBlue,
    secondaryNavyOne: swatch.secondaryNavyOne,
    secondaryNavyThree: swatch.secondaryNavyThree,
    solitude: swatch.solitude,
    darkCyanBlue: swatch.darkCyanBlue,
    yellow: swatch.yellow,
    lightGreen: swatch.lightGreen,
    alertMsgRed: swatch.alertMsgRed,
    blue: swatch.blue,
    rockBlue: swatch.rockBlue,
    lightGrey: swatch.grey.lightGrey,
    darkGrey: swatch.grey.darkGrey,
    gainsboro: swatch.grey.gainsboro,
    graySwan: swatch.grey.graySwan,
    lighterGrey: swatch.grey.lighter,
    lighterGrayShade: swatch.grey.lighterGrayShade,
    dimGray: swatch.grey.dimGray,
    lightBlack: "#353432",
    secondaryBtn: "#0D70B7",
    secondaryBg: "#EDF4F8",
    lightPurple: swatch.lightPurple,
    lightGrayishBlue: swatch.lightGrayishBlue,

    primary: {
        light: "#3CA865",
        base: "#266B40",
        dark: "#1B4D2E",
        contrast: "white",
    },
    secondary: {
        light: "#D10063",
        base: "#8F0446",
        dark: "#660332",
        contrast: "white",
    },
    tertiary: {
        light: "#676767",
        base: "#ED9C00",
        dark: "#676767",
        contrast: "black",
    },
    success: "#3CB56F",
    warning: "#E8AA0F",
    error: "#E23939",
    info: {
        base: "#3D3D3D",
    },
    background: {
        base: swatch.white,
        black: swatch.black,
        yellow: swatch.yellow,
        mango: swatch.mango,
        red: swatch.red,
        whiteSmoke: swatch.whiteSmoke,
        whiteSnow: swatch.whiteSnow,
        whiteSaltpan: swatch.whiteSaltpan,
        whisper: swatch.whisper,
        darkWhite: swatch.darkWhite,
        primaryBlue: swatch.primaryBlue,
        secondaryNavyThree: swatch.secondaryNavyThree,
        secondaryNavyOne: swatch.secondaryNavyOne,
        lighterGrayShade: swatch.grey.lighterGrayShade,
        grayShade: swatch.grey.grayShade,
        gainsboro: swatch.grey.gainsboro,
        grayWhisper: swatch.grey.grayWhisper,
        darkGray: swatch.grey.darkGray,
        lightGrey: swatch.grey.lightGrey,
        alt: swatch.grey.lighter,
        hover: swatch.grey.light,
        tag: swatch.grey.base,
        shade: swatch.grey.shade,
    },
    border: {
        base: "#e2e3e6",
        dark: "#5d5f62",
        light: "#BDBBBD",
        darkShade: swatch.grey.dark,
        darker: swatch.grey.darker,
    },
    disabled: {
        lighter: "#e2e3e6",
        light: swatch.grey.light,
        base: "#939393",
        dark: swatch.grey.dark,
    },
    text: {
        lighter: swatch.grey.base,
        light: swatch.grey.dark,
        base: swatch.grey.darker,
        erpNumber: "#666",
        mineShaft: "#262525",
    },
    link: {
        base: "#0F96D9",
        hover: swatch.grey.dark,
    },
    shadow: {
        light: "rgba(48, 49, 51, 0.05)",
        base: "rgba(48, 49, 51, 0.1)",
    },
    pagination: {
        base: swatch.grey.light,
        hover: Color(swatch.grey.light).darken(0.15).hex(),
    },
    outline: "#1d8ed5",
};

// -- Typography

// const fallbackFont = "Poppins";

export const TYPOGRAPHY = {
    fontFamily: {
        title: `'Poppins'`,
        base: `'Poppins'`,
        dosis: `'Dosis'`,
        montserrat: `'Montserrat'`,
        barlow: `'Barlow'`,
    },
    weight: {
        light: 300,
        base: 400,
        baseBold: 500,
        mediumbold: 600,
        bold: 700,
        black: 800,
    },
    size: {
        veryTiny: "10px",
        vTiny: "11px",
        tiny: "12px",
        smallest: "13px",
        smaller: "14px",
        small: "15px",
        base: "16px",
        mbase: "17px",
        large: "18px",
        larger: "20px",
        mLargest: "23px",
        largest: "24px",
        xlargest: "26px",
        mLarge: "28px",
        xlarge: "30px",
        xbase: "32px",
        biggest: "36px",
        xlarger: "40px",
    },
    letterSpacing: {
        smallest: "0px",
        vSmaller: "0.5px",
        vSmallish: "0.6px",
        smaller: "1px",
        smallish: "1.5px",
        vSmall: "1.15px",
        mSmall: "1.25px",
        small: "2px",
    },
    lineHeight: {
        smaller: 1,
        vSmall: 1.15,
        small: 1.25,
        vSmallest: 1.5,
        large: 2,
        vSmallestbase: "16px",
        mSmallestbase: "18px",
        smallest: "19px",
        smallestbase: "20px",
        smallbase: "23px",
        base: "24px",
        mbase: "28px",
        xbase: "32px",
        xlarge: "38px",
        xlarger: "42px",
    },
    spacing: {
        small: "0.5em",
        base: "1em",
        large: "1.5em",
        larger: "2em",
        largest: "3em",
    },
};

// -- Spacings
export const SPACING = {
    xxxlarge: "4em",
    xxlarge: "3em",
    xlarge: "2em",
    large: "1.5em",
    medium: "1.25em",
    standard: "1em",
    small: ".75em",
    xsmall: ".5em",
    xxsmall: ".25em",
    none: "0",
};

// -- Product

const productImageSizes = {
    small: {
        width: 311,
        height: 193,
    },
    medium: {
        width: 690,
        height: 429,
    },
    large: {
        width: 1164,
        height: 724,
    },
};

export const PRODUCT = {
    image: {
        ...productImageSizes,
        ratio: productImageSizes.large.height / productImageSizes.large.width,
    },
};

// -- Common

const contentWidth = 1200;

const spacing = {
    side: ["20px", "30px", "45px", "45px", "45px"],
    content: ["25px", "30px", "45px", "16px", "60px"],
    narrowContent: ["25px", "30px", "40px", "40px", "50px"],
};

const largestSideSpacing = spacing.side.filter(x => x).pop() as any;
// const siteWidth = contentWidth + (parseNumber(largestSideSpacing) ?? 0) * 2;
const siteWidth = 1200;

export const COMMON = {
    breakpoints: [0, 576, 768, 992, contentWidth],
    width: {
        content: contentWidth,
        site: "100%",
    },
    spacing,
    outline: {
        width: "2px",
        style: "solid",
    },
    defaultProductImagePath: `${STATIC_PATH}/product-not-available.svg`,
    defaultCategoryImagePath: `${STATIC_PATH}/category-not-available.svg`,
};

// -- Menu

export const MENU = {
    mobileButtonSpacing: ".25em",
    topNavItemSpacing: ".8em",
};

// -- Form

export const FORM = {
    field: {
        padding: "1em",
        spacing: "1em",
    },
    button: {
        spacing: "1.6em",
    },
};
